import { Node } from 'prosemirror-model'

import { constants } from '@showrunner/prose-schemas'

import { BlockFormats } from '#util/formats'
import { PageLayout } from '#util/PageLayout'
import { getBlockHeightInfo, Line, Section } from '#util/pagination/helpers'

const { NodeTypeMap } = constants

type LineCount = number
type Position = number

export enum PageBreakType {
  DialogueContinuation = 'dialogue-continuation',
  ManualBreak = 'manual-break',
  Normal = 'normal',
}

export type PaginationAction =
  | { kind: 'break-before'; breakType?: PageBreakType }
  | {
      kind: 'split-and-break'
      breakType: PageBreakType.DialogueContinuation | PageBreakType.Normal
      pos: Position
      linesBefore: LineCount
    }
  | { kind: 'place-block' }
  | { kind: 'force-fit-block' }

export interface PaginationBlockInfo {
  node: Node
  pos: Position
  contentHeight: LineCount
  spaceAbove: LineCount
  manualBreak: boolean
  sections: Section[]
}

export interface PaginationPageContext {
  used: LineCount
  remaining: LineCount
}

export interface PaginationBlockContext {
  node: Node
  pos: Position
  contentHeight: LineCount
  spaceAbove: LineCount
  manualBreak: boolean
  resumePos: Position
  overflows: boolean
  sections: Section[]
}

export interface PaginationContext {
  block: PaginationBlockContext
  page: PaginationPageContext
  peek(offset: number): PaginationBlockInfo | null
}

export abstract class PaginationPolicy {
  decide(_ctx: PaginationContext): PaginationAction | null {
    return null
  }
}

export class DefaultFlowPaginationPolicy extends PaginationPolicy {
  static readonly instance: DefaultFlowPaginationPolicy =
    new DefaultFlowPaginationPolicy()

  override decide(ctx: PaginationContext): PaginationAction {
    if (!ctx.block.overflows) return { kind: 'place-block' }
    if (ctx.page.used > 0) return { kind: 'break-before' }
    return { kind: 'force-fit-block' }
  }
}

// minimal page info needed for pagination and page boundary rendering
export interface BasePage {
  start: number
  end: number
  startBlock: Node
  endBlock: Node
  lines: Line[]
  breakType?: PageBreakType
}

// contentHeight + shortfall should equal the page height (in lines) as defined by the page layout.
export interface Page extends BasePage {
  contentHeight: LineCount
  // number of lines by which the content height falls short of the page height.
  // this is used to determine if a page is underfull and to apply vertical justification if needed.
  shortfall: LineCount
}

export class NodePagination<T extends Page = Page> {
  constructor(
    readonly pages: T[],
    readonly node: Node,
  ) {}

  get isTitlePage(): boolean {
    return this.node?.type.name === NodeTypeMap.TITLE_PAGE
  }
}

export interface Paginator {
  init(pos: number, node: Node, policy?: PaginationPolicy): NodePagination
  apply(pos: number, node: Node, policy?: PaginationPolicy): NodePagination
}

export class SimplePaginator implements Paginator {
  constructor(
    private blockFormats: BlockFormats,
    private pageLayout: PageLayout,
  ) {}

  static from(
    blockFormats: BlockFormats,
    pageLayout: PageLayout,
  ): SimplePaginator {
    return new SimplePaginator(blockFormats, pageLayout)
  }

  init(
    pos: number,
    node: Node,
    policy: PaginationPolicy = DefaultFlowPaginationPolicy.instance,
  ): NodePagination {
    return this.paginate(pos, node, policy)
  }

  apply(
    pos: number,
    node: Node,
    policy: PaginationPolicy = DefaultFlowPaginationPolicy.instance,
  ): NodePagination {
    return this.paginate(pos, node, policy)
  }

  private paginate(
    pagePos: number,
    pageNode: Node,
    policy: PaginationPolicy,
  ): NodePagination {
    const rawBlocks = collectPageBlocks(pageNode, pagePos)
    const blocks = measureBlocks(rawBlocks, this.blockFormats)
    const pageContentEnd = pagePos + 1 + pageNode.content.size
    const { maxLinesPerPage } = this.pageLayout

    const pages: Page[] = []
    let pageStart = pagePos + 1
    let usedLines: LineCount = 0
    let pageStartBlock = blocks[0]?.node
    let lines: Line[] = []

    const flushPage = (
      end: Position,
      endBlock: Node,
      shortfall: LineCount,
      contentHeight: LineCount,
      breakType: PageBreakType = PageBreakType.Normal,
    ) => {
      pages.push({
        start: pageStart,
        end,
        startBlock: pageStartBlock,
        endBlock,
        contentHeight,
        shortfall,
        lines,
        breakType,
      })
      pageStart = end
      usedLines = 0
      contentHeight = 0
      lines = []
    }

    const buildCtx = (
      i: number,
      entry: PaginationBlockInfo,
      metrics: BlockMetrics,
    ): PaginationContext => ({
      block: {
        node: entry.node,
        pos: entry.pos,
        contentHeight: entry.contentHeight,
        spaceAbove: metrics.spaceAbove,
        sections: entry.sections,
        manualBreak: entry.manualBreak,
        resumePos: metrics.resumePos,
        overflows: metrics.overflows,
      },
      page: {
        remaining: maxLinesPerPage - usedLines,
        used: usedLines,
      },
      peek(offset: number): PaginationBlockInfo | null {
        const idx = i + offset
        return idx >= 0 && idx < blocks.length ? blocks[idx] : null
      },
    })

    let i = 0
    while (i < blocks.length) {
      let entry = blocks[i]
      if (withinBlockContent(entry, pageStart)) {
        const blockInfo = getBlockHeightInfo(
          entry.node,
          this.blockFormats,
          pageStart - entry.pos - 1,
        )
        entry = {
          ...entry,
          pos: pageStart - 1,
          manualBreak: false,
          contentHeight: blockInfo.contentHeight,
          sections: blockInfo.sections ?? [],
        }
      }

      const metrics = computeBlockMetrics(
        entry,
        pageStart,
        usedLines,
        maxLinesPerPage,
      )
      const ctx = buildCtx(i, entry, metrics)
      const shortfall = maxLinesPerPage - usedLines

      const action = policy.decide(ctx)
      if (!action) {
        throw new Error('Pagination policy returned no action')
      }

      switch (action.kind) {
        // insert a break before the block and continue placing the block on the next page
        case 'break-before':
          flushPage(
            entry.pos,
            blocks[i - 1]?.node ?? entry.node,
            shortfall,
            usedLines,
            action.breakType,
          )
          pageStartBlock = entry.node
          continue

        // insert a break at the split point and continue placing the block on the next page
        case 'split-and-break': {
          const splitLines =
            usedLines === 0
              ? action.linesBefore
              : metrics.spaceAbove + action.linesBefore
          lines = [
            ...lines,
            ...spaceAsLines(metrics.spaceAbove),
            ...collectLinesUpToOffset(
              entry.sections,
              action.pos - entry.pos - 1,
              entry.pos + 1,
            ),
          ]
          flushPage(
            action.pos,
            entry.node,
            maxLinesPerPage - (usedLines + splitLines),
            usedLines + splitLines,
            action.breakType,
          )
          pageStartBlock = entry.node
          continue
        }

        // place the block on the current page and continue to the next block
        case 'place-block':
          lines = [
            ...lines,
            ...spaceAsLines(metrics.spaceAbove),
            ...toDocLines(entry.sections, entry.pos + 1),
          ]
          usedLines += metrics.needed
          i += 1
          continue

        // force-fit the block on the current page and continue to the next block.
        // this will avoid infinite loop when a block is taller than any page.
        case 'force-fit-block':
          lines = [
            ...lines,
            ...spaceAsLines(metrics.spaceAbove),
            ...toDocLines(entry.sections, entry.pos + 1),
          ]
          usedLines += metrics.needed
          i += 1
          continue
      }
    }

    flushPage(
      pageContentEnd,
      blocks.at(-1)?.node ?? pageStartBlock,
      maxLinesPerPage - usedLines,
      usedLines,
    )

    return new NodePagination(pages, pageNode)
  }
}

function collectPageBlocks(
  pageNode: Node,
  pagePos: number,
): Array<{ node: Node; pos: number }> {
  const blocks: Array<{ node: Node; pos: number }> = []

  pageNode.forEach((node, offset) => {
    blocks.push({
      node,
      pos: pagePos + 1 + offset,
    })
  })

  return blocks
}

function measureBlocks(
  blocks: Array<{ node: Node; pos: number }>,
  blockFormats: BlockFormats,
): PaginationBlockInfo[] {
  return blocks.map(({ node, pos }) => {
    const { contentHeight, spaceAbove, sections, manualBreak } =
      getBlockHeightInfo(node, blockFormats)

    return {
      node,
      pos,
      contentHeight,
      spaceAbove,
      sections: sections ?? [],
      manualBreak,
    }
  })
}

interface BlockMetrics {
  resumePos: number
  needed: number
  spaceAbove: number
  overflows: boolean
}

function computeBlockMetrics(
  entry: PaginationBlockInfo,
  pageStart: number,
  usedLines: number,
  maxLinesPerPage: number,
): BlockMetrics {
  const { pos, contentHeight } = entry
  const resumePos = withinBlockContent(entry, pageStart) ? pageStart : pos
  const remaining = contentHeight
  const isStartOfPage = usedLines === 0
  const spaceAbove = isStartOfPage && resumePos >= pos ? 0 : entry.spaceAbove
  const needed = isStartOfPage ? remaining : spaceAbove + remaining

  return {
    resumePos,
    needed,
    spaceAbove,
    overflows: usedLines + needed > maxLinesPerPage,
  }
}

function withinBlockContent(entry: PaginationBlockInfo, pos: number): boolean {
  return pos > entry.pos && pos < entry.pos + entry.node.nodeSize
}

// Space-above text lines have no document position — they represent blank margin lines above a block.
function spaceAsLines(count: number): Line[] {
  return Array.from({ length: count }, () => ({
    text: '',
    width: 0,
    from: Infinity,
    to: Infinity,
  }))
}

// Convert section-local line positions to doc positions.
// blockDocOffset = entry.pos + 1 (start of block content in the document).
function toDocLines(sections: Section[], blockDocOffset: number): Line[] {
  const result: Line[] = []
  let sectionStart = 0
  for (const section of sections) {
    for (const line of section) {
      result.push({
        ...line,
        from: blockDocOffset + sectionStart + line.from,
        to: blockDocOffset + sectionStart + line.to,
      })
    }
    sectionStart += (section.at(-1)?.to ?? 0) + 1
  }
  return result
}

// Collect lines from sections up to splitOffset (exclusive), with doc positions.
// splitOffset is the text offset within the sections (action.pos - entry.pos - 1).
// blockDocOffset = entry.pos + 1.
// If the split lands mid-line, that line is trimmed to only the characters before it.
function collectLinesUpToOffset(
  sections: Section[],
  splitOffset: number,
  blockDocOffset: number,
): Line[] {
  const result: Line[] = []
  let sectionStart = 0

  for (const section of sections) {
    const localSplit = splitOffset - sectionStart
    for (const line of section) {
      if (line.from >= localSplit) return result
      if (line.to > localSplit) {
        const trimLen = localSplit - line.from
        return [
          ...result,
          {
            ...line,
            text: line.text.slice(0, trimLen),
            from: blockDocOffset + sectionStart + line.from,
            to: blockDocOffset + sectionStart + localSplit,
            width: trimLen,
          },
        ]
      }
      result.push({
        ...line,
        from: blockDocOffset + sectionStart + line.from,
        to: blockDocOffset + sectionStart + line.to,
      })
    }
    sectionStart += (section.at(-1)?.to ?? 0) + 1
  }

  return result
}
